"use client";

import React, { useMemo } from "react";
import type { IWatchlistNewsProps } from "../../../interfaces/NewsFilter.interface";
import NewsList from "../components/NewsList";
import NewsEmptyState from "../components/NewsEmptyState";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";
import List from "../components/List";
import { usePersonalizedNews } from "../hooks/usePersonalizedNews";
import NoResultsFound from "../../NoResultsFound";
import { handleRedirection } from "../../../utils/helper";
import { useRouter } from "next/navigation";
import { buildFilterArray } from "../utils/filterUtils";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import { trackComponent } from "@repo/analytics";
import {
  campaignName,
  journeyTag,
  productCategory,
  referenceId,
  sprint,
  subSection,
} from "../../../pages/News/newsTrackingConstants";
import { trackFailureScenarioRetryClick } from "../components/trackFailureScenarioRetry";

const WatchlistNews: React.FC<IWatchlistNewsProps> = ({ filters, aemContent }) => {
  const router = useRouter();
  const {
    newsData,
    isLoading,
    page,
    handleShowMore,
    handleShowLess,
    refetch,
  } = usePersonalizedNews({
    newsType: "watchlist",
    filters,
    limit: aemContent?.limit ? parseInt(aemContent.limit) : 10,
  });
  const filter = useMemo(() => buildFilterArray(filters), [filters]);

  const handleWatchlistRedirection = () => {
    handleRedirection(aemContent?.watchlist?.redirectionUrl ?? "", router);
  };

  const handleWatchlistCtaClick = async () => {
    const ctaText =
      aemContent?.watchlist?.emptyWatchlistButtonCta ?? "Add stocks";

    try {
      const customerId = await getCustomerIdForAnalytics();

      await trackComponent({
        componentName: "watchlist",
        sectionTitle: "News",
        eventType: "watchlist_button_click",
        ctaText,
        productCategory,
        subSection,
        cleverTapOverrides: {
          EP_JOURNEY: journeyTag,
          EP_PROPERTY_TYPE: "plp",
          EP_PRODUCT_NAME: subSection,
          EP_CAMPAIGN: campaignName,
          EP_SPRINT: sprint,
          EP_REFERENCE_ID: referenceId,
          ...(customerId && { EP_CUSTOMER_ID: customerId }),
          EP_SR_NUMBER: "1",
          EP_CUST_ATTRIBUTE_1: "empty_selection",
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to track watchlist button click", error);
    }

    handleWatchlistRedirection();
  };

  if (isLoading && page === 1) {
    return (
      <div className="px-5 py-4">
        {
          [...Array(5)].map((_, index) =>
            <BFLSkeleton key={index} height={70} classes="mb-2" />)
        }
      </div>
    );
  }
  if (newsData.statusCode !== 90) {
    return (
      <div className="px-5 py-4">
        <BFLApiFailure
          error={aemContent?.apiFailureTitle ?? "Unable to fetch news"}
          errorDescription={aemContent?.apiFailureDescription ?? "There was an issue fetching news for your watchlist. Please try again later."}
          retryButtonText={aemContent?.apiFailureButtonCta ?? "Retry"}
          imageSrc={aemContent?.images?.errorImage}
          imageAlt={aemContent?.apiFailureImageAlt ?? "Error"}
          onRetry={refetch}
          trackingConfig={{
            sectionTitle: `${aemContent?.apiFailureTitle ?? "Watchlist"} unable to load`,
            componentPosition: "2",
            entryPoint: "no_result_found",
            productCategory,
            subSection,
            journeyTag,
            campaignName,
            sprint,
            referenceId,
          }}
          onRetryClick={(params) =>
            trackFailureScenarioRetryClick(params, getCustomerIdForAnalytics)
          }
        />
      </div>
    );
  }


  if (filter.length === 0 && (!newsData?.list || newsData.list.length === 0)) {
    return (
      <div className="px-5 py-4">
        <NoResultsFound
          title={aemContent?.watchlist.emptyWatchlistTitle}
          description={aemContent?.watchlist.emptyWatchlistDescription}
          buttonText={aemContent?.watchlist.emptyWatchlistButtonCta}
          buttonIcon={aemContent?.watchlist.emptyWatchlistButtonIcon}
            onButtonClick={handleWatchlistCtaClick}
          aemImages={aemContent?.images}
        />
      </div>
    );
  }

  if (newsData.news.length === 0) {
    return (
      <div className="px-4 py-4">
        <NewsEmptyState
          filters={filters}
          title={aemContent?.watchlist.noResultTitle ?? "No news found for your watchlist"}
          description={aemContent?.watchlist.noResultFilterDescription ?? "Your current filter selection did not match any results."}
          emptyDescription={aemContent?.watchlist?.noResultDescription ?? "No watchlist news available at the moment."}
          aemImages={aemContent?.images}
        />
      </div>
    );
  }


  return (
    <div className="px-5 py-4">
      <List showMoreText={aemContent?.showMoreText} showLessText={aemContent?.showLessText} showLess={newsData.showLess} showMore={newsData.showMore} onShowLess={handleShowLess} onShowMore={handleShowMore}>
        <NewsList
          newsItems={newsData.news}
          variant="compact"
          showCompanies={true}
          timestampLabels={aemContent?.timestampLabels}
        />
        {isLoading && page > 1 && [...Array(5)].map((_, index) =>
          <BFLSkeleton key={index} height={70} classes="mb-2" />)}
      </List>
    </div>
  );
};

export default WatchlistNews;

